import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { Screens } from '~/constants/config/permissions';
import { QcFullPage } from '~/features/content/components/qc/qc-full-page';
import { QcPageSkeleton } from '~/features/content/components/qc/qc-page-skeleton';
import { loadQcSetupPage } from '~/features/content/components/qc/qc-preload';
import { requirePermission } from '~/utils/guards';

// Shared with the Jobs tab's hover preload, so a preloaded chunk is the one this renders.
const QcSetupPage = lazy(loadQcSetupPage);

/** Optional prefill: start from a job's products (e.g. "Run QC on this job"). */
const searchSchema = z.object({
  fromRun: z.string().optional().catch(undefined),
  // Start with "Redo detection & attributes" on (links like "Run again and redo them").
  reextract: z.boolean().optional().catch(undefined),
});

/** QC step 1 — Set up a new QC check, at `/content/qc/new` — a full page outside the Content tabs. */
export const Route = createFileRoute('/_authenticated/_tenant/content_/qc/new')({
  // Outside the Content layout (its own full page), so it repeats the section's screen guard.
  beforeLoad: requirePermission(Screens.CONTENT_GENERATION),
  validateSearch: searchSchema,
  component: QcSetupRoute,
});

function QcSetupRoute() {
  const { fromRun, reextract } = Route.useSearch();
  return (
    <QcFullPage>
      <Suspense fallback={<QcPageSkeleton variant="setup" />}>
        {/* Keyed by the source job (and redo), so moving to another `fromRun` starts fresh. */}
        <QcSetupPage
          key={`${fromRun ?? ''}:${reextract ? 'redo' : ''}`}
          fromRun={fromRun}
          reextract={reextract ?? false}
        />
      </Suspense>
    </QcFullPage>
  );
}
